/**
 * The journal's grouping: chapters, in reading order, cut into in-game days.
 *
 * Grouping by day rather than by chapter is the whole point of the feature. A chapter is a
 * unit of *interaction* and means nothing to a player looking back — "chapter 14" is not a
 * memory. A day is a unit of *survival*, and it is also the axis the game's cruellest mechanics
 * run on: hunger and thirst tick on the clock, so "day 3" is the frame in which a run makes
 * sense.
 *
 * Consecutive runs, not a keyed bucket: in-game days only ever move forward, so two separate
 * groups labelled "Day 3" cannot occur — and if the engine ever did rewind the clock, showing
 * the story out of order would be a worse lie than showing the day twice.
 */
import type { Chapter } from '@/api/generated';

export interface JournalDay {
  day: number;
  chapters: Chapter[];
}

/** @param chapters oldest first, as `toReadingOrder` returns them. */
export function toJournalDays(chapters: readonly Chapter[]): JournalDay[] {
  const days: JournalDay[] = [];

  for (const chapter of chapters) {
    const current = days.at(-1);
    if (current && current.day === chapter.world_time.day) current.chapters.push(chapter);
    else days.push({ day: chapter.world_time.day, chapters: [chapter] });
  }

  return days;
}
